import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaCamera, FaInfoCircle } from "react-icons/fa";
import myConfig from "../../configs/config";
import SidebarToolInfoModal from "../../components/SidebarToolInfoModal";
import "./MachineChecklistEditor.css";

export const DEFAULT_CHECKLIST_EXAMPLES = [
  "Nível de óleo do motor",
  "Pressão dos pneus",
  "Filtro de ar da cabine",
  "Limpeza do filtro de ar do motor",
  "Drenar água no filtro de combustível",
  "Conferência externa",
  "Rodas e porcas (sem folga)",
  "Limpeza da cabine",
  "Conferência do nível de fluido de arrefecimento do radiador",
];

const TIRE_PRESSURE_NAME = "Pressão dos pneus";

const EMPTY_TIRE_PRESSURE = {
  unit: "lb",
  sameAll: false,
  differInnerOuter: false,
  front: "",
  rear: "",
  frontInner: "",
  frontOuter: "",
  rearInner: "",
  rearOuter: "",
};

function newItemId() {
  return `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function parseInterval(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(String(value).replace(",", "."));
  if (!Number.isFinite(number) || number <= 0) return null;
  return Math.round(number * 100) / 100;
}

function checklistItem(item, overrides = {}) {
  const interval =
    overrides.interval !== undefined
      ? parseInterval(overrides.interval)
      : parseInterval(
          item.interval != null ? item.interval : item.interval_hours
        );
  const mandatory =
    overrides.mandatory !== undefined
      ? Boolean(overrides.mandatory)
      : item.mandatory === undefined
        ? true
        : Boolean(item.mandatory);
  return {
    id: item.id || newItemId(),
    label: overrides.label !== undefined ? overrides.label : item.label,
    require_photo:
      overrides.require_photo !== undefined
        ? Boolean(overrides.require_photo)
        : Boolean(item.require_photo),
    mandatory,
    interval,
  };
}

function sameLabel(a, b) {
  return (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
}

function normalizeExampleLabel(label) {
  if (sameLabel(label, "Nível de óleo")) return "Nível de óleo do motor";
  if (sameLabel(label, "Limpeza dos filtros")) return "Limpeza da cabine";
  if (sameLabel(label, "Filtro de ar do motor")) {
    return "Limpeza do filtro de ar do motor";
  }
  if (sameLabel(label, "Água no filtro de combustível")) {
    return "Drenar água no filtro de combustível";
  }
  return label;
}

function isRetiredClimateItem(label) {
  return sameLabel(label, "Regulagem de ar / climatização");
}

function isTirePressureLabel(label) {
  return /^press[aã]o dos pneus/i.test((label || "").trim());
}

function catalogLabel(item) {
  return isTirePressureLabel(item.label) ? TIRE_PRESSURE_NAME : item.label;
}

function parseTirePressureLabel(label) {
  const text = (label || "").trim();
  const unitMatch = text.match(/\b(lb|psi|bar)\b/i);
  const unit = unitMatch ? unitMatch[1].toLowerCase() : "lb";
  const numbers = (text.match(/\d+(?:[.,]\d+)?/g) || []).map((value) =>
    value.replace(",", ".")
  );

  if (/todas/i.test(text) && numbers[0]) {
    return { ...EMPTY_TIRE_PRESSURE, unit, sameAll: true, front: numbers[0] };
  }
  if (/int\.|interno/i.test(text) && numbers.length >= 4) {
    return {
      ...EMPTY_TIRE_PRESSURE,
      unit,
      differInnerOuter: true,
      frontInner: numbers[0],
      frontOuter: numbers[1],
      rearInner: numbers[2],
      rearOuter: numbers[3],
    };
  }
  if (numbers.length >= 2) {
    return {
      ...EMPTY_TIRE_PRESSURE,
      unit,
      front: numbers[0],
      rear: numbers[1],
    };
  }
  if (numbers[0]) {
    return { ...EMPTY_TIRE_PRESSURE, unit, sameAll: true, front: numbers[0] };
  }
  return { ...EMPTY_TIRE_PRESSURE, unit };
}

function formatTirePressureLabel(draft) {
  const unit = draft.unit || "lb";
  if (draft.sameAll) {
    return `${TIRE_PRESSURE_NAME}: ${draft.front} ${unit} (todas)`;
  }
  if (draft.differInnerOuter) {
    return (
      `${TIRE_PRESSURE_NAME}: diant. int. ${draft.frontInner} ${unit}` +
      ` / ext. ${draft.frontOuter} ${unit};` +
      ` tras. int. ${draft.rearInner} ${unit}` +
      ` / ext. ${draft.rearOuter} ${unit}`
    );
  }
  return (
    `${TIRE_PRESSURE_NAME}: dianteira ${draft.front} ${unit}` +
    ` · traseira ${draft.rear} ${unit}`
  );
}

function tirePressureIsValid(draft) {
  const filled = (value) => String(value || "").trim() !== "";
  if (draft.sameAll) return filled(draft.front);
  if (draft.differInnerOuter) {
    return (
      filled(draft.frontInner) &&
      filled(draft.frontOuter) &&
      filled(draft.rearInner) &&
      filled(draft.rearOuter)
    );
  }
  return filled(draft.front) && filled(draft.rear);
}

function MachineChecklistEditor({
  farmId,
  items,
  enabled,
  onEnabledChange,
  onChange,
  meterType = "hour_meter",
  t,
}) {
  const intervalUnit =
    meterType === "odometer" ? "km" : meterType === "both" ? "h/km" : "h";
  const [newLabel, setNewLabel] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [tirePressureItem, setTirePressureItem] = useState(null);
  const [tirePressureDraft, setTirePressureDraft] = useState(EMPTY_TIRE_PRESSURE);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateMessage, setTemplateMessage] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("client-token")}`,
    }),
    []
  );

  const selectedTireItem = items.find((item) => isTirePressureLabel(item.label));

  function templateRows(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data && data.results)) return data.results;
    return [];
  }

  async function loadTemplates() {
    try {
      const response = await axios.get(
        `${myConfig.API_URL}/checklist-template/?farm_id=${farmId}`,
        { headers: authHeaders }
      );
      setTemplates(templateRows(response.data));
    } catch (error) {
      setTemplates([]);
    }
  }

  function isSelected(item) {
    if (isTirePressureLabel(item.label)) {
      return Boolean(selectedTireItem);
    }
    return items.some(
      (selected) =>
        selected.id === item.id || sameLabel(selected.label, item.label)
    );
  }

  function selectedFor(item) {
    if (isTirePressureLabel(item.label) && selectedTireItem) {
      return selectedTireItem;
    }
    return items.find(
      (row) => row.id === item.id || sameLabel(row.label, item.label)
    );
  }

  function itemRequiresPhoto(item) {
    const selected = selectedFor(item);
    return Boolean((selected && selected.require_photo) || item.require_photo);
  }

  function itemIsMandatory(item) {
    const selected = selectedFor(item);
    if (selected && selected.mandatory !== undefined) {
      return Boolean(selected.mandatory);
    }
    if (item.mandatory === undefined) return true;
    return Boolean(item.mandatory);
  }

  function itemInterval(item) {
    const selected = selectedFor(item);
    if (selected && selected.interval != null) return selected.interval;
    return item.interval != null ? item.interval : null;
  }

  function patchSelectedItem(item, overrides) {
    const updated = checklistItem(item, overrides);
    persistCatalog(
      catalog.map((row) => (row.id === item.id ? updated : row))
    );
    if (!isSelected(item)) return;
    onChange(
      items.map((selected) => {
        const sameTire =
          isTirePressureLabel(item.label) && isTirePressureLabel(selected.label);
        if (
          selected.id === item.id ||
          sameLabel(selected.label, item.label) ||
          sameTire
        ) {
          return checklistItem(selected, overrides);
        }
        return selected;
      })
    );
  }

  async function persistCatalog(nextCatalog) {
    const stored = nextCatalog.map((item) =>
      checklistItem(
        item,
        isTirePressureLabel(item.label) ? { label: TIRE_PRESSURE_NAME } : {}
      )
    );
    setCatalog(stored);
    try {
      await axios.post(
        `${myConfig.API_URL}/checklist-catalog/`,
        { farm: Number(farmId), items: stored },
        { headers: authHeaders }
      );
    } catch (error) {
      // A lista continua na tela; o save da máquina ainda guarda o que estiver destacado.
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const response = await axios.get(
          `${myConfig.API_URL}/checklist-catalog/?farm_id=${farmId}`,
          { headers: authHeaders }
        );
        if (cancelled) return;
        const saved = Array.isArray(response.data.items)
          ? response.data.items
          : [];
        const merged = saved.length
          ? saved.map((item) =>
              isTirePressureLabel(item.label)
                ? { ...item, label: TIRE_PRESSURE_NAME }
                : { ...item, label: normalizeExampleLabel(item.label) }
            )
          : DEFAULT_CHECKLIST_EXAMPLES.map((label) => ({
              id: newItemId(),
              label,
            }));
        DEFAULT_CHECKLIST_EXAMPLES.forEach((label) => {
          if (!merged.some((row) => sameLabel(row.label, label))) {
            merged.push({ id: newItemId(), label });
          }
        });
        items.forEach((item) => {
          if (isTirePressureLabel(item.label)) {
            if (!merged.some((row) => isTirePressureLabel(row.label))) {
              merged.push({ ...item, label: TIRE_PRESSURE_NAME });
            }
            return;
          }
          const label = normalizeExampleLabel(item.label);
          if (!merged.some((row) => sameLabel(row.label, label))) {
            merged.push({ ...item, label });
          }
        });
        const unique = [];
        merged.forEach((item) => {
          if (isRetiredClimateItem(item.label)) return;
          if (!unique.some((row) => sameLabel(row.label, item.label))) {
            unique.push(item);
          }
        });
        setCatalog(unique);
        const savedLabels = saved.map((item) => item.label).join("|");
        const nextLabels = unique.map((item) => item.label).join("|");
        if (unique.length !== saved.length || savedLabels !== nextLabels) {
          persistCatalog(unique);
        }
        const nextItems = items
          .filter((item) => !isRetiredClimateItem(item.label))
          .map((item) => ({
            ...item,
            label: normalizeExampleLabel(item.label),
          }));
        const itemsChanged =
          nextItems.length !== items.length ||
          nextItems.some((item, index) => item.label !== items[index].label);
        if (itemsChanged) {
          onChange(nextItems);
        }
      } catch (error) {
        if (!cancelled) {
          setCatalog(items);
        }
      }
    }
    load();
    loadTemplates();
    return () => {
      cancelled = true;
    };
    // Só na abertura da máquina: a lista da fazenda não deve resetar a cada clique.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders, farmId]);

  function openTirePressurePopup(item) {
    setTirePressureItem(item);
    setTirePressureDraft(
      selectedTireItem
        ? parseTirePressureLabel(selectedTireItem.label)
        : { ...EMPTY_TIRE_PRESSURE }
    );
  }

  function closeTirePressurePopup() {
    setTirePressureItem(null);
    setTirePressureDraft(EMPTY_TIRE_PRESSURE);
  }

  function ensureChecklistEnabled() {
    if (!enabled) onEnabledChange(true);
  }

  function toggleItem(item) {
    if (isTirePressureLabel(item.label)) {
      if (isSelected(item)) {
        onChange(items.filter((selected) => !isTirePressureLabel(selected.label)));
        return;
      }
      ensureChecklistEnabled();
      openTirePressurePopup(item);
      return;
    }
    if (isSelected(item)) {
      onChange(
        items.filter(
          (selected) =>
            selected.id !== item.id && !sameLabel(selected.label, item.label)
        )
      );
      return;
    }
    ensureChecklistEnabled();
    onChange([...items, checklistItem(item)]);
  }

  function toggleRequirePhoto(event, item) {
    event.stopPropagation();
    ensureChecklistEnabled();
    const nextRequirePhoto = !itemRequiresPhoto(item);
    if (!isSelected(item)) {
      persistCatalog(
        catalog.map((row) =>
          row.id === item.id
            ? checklistItem(item, { require_photo: nextRequirePhoto })
            : row
        )
      );
      onChange([...items, checklistItem(item, { require_photo: nextRequirePhoto })]);
      return;
    }
    patchSelectedItem(item, { require_photo: nextRequirePhoto });
  }

  function toggleMandatory(event, item) {
    event.stopPropagation();
    if (!isSelected(item)) return;
    patchSelectedItem(item, { mandatory: !itemIsMandatory(item) });
  }

  function changeInterval(event, item) {
    event.stopPropagation();
    if (!isSelected(item)) return;
    patchSelectedItem(item, { interval: event.target.value });
  }

  function addItem(label) {
    const trimmed = (label || "").trim();
    if (!trimmed) return;
    const existing = catalog.find((item) =>
      isTirePressureLabel(trimmed)
        ? isTirePressureLabel(item.label)
        : sameLabel(item.label, trimmed)
    );
    if (existing) {
      if (isTirePressureLabel(existing.label)) {
        openTirePressurePopup(existing);
      } else if (!isSelected(existing)) {
        toggleItem(existing);
      }
      setNewLabel("");
      return;
    }
    const created = checklistItem({
      id: newItemId(),
      label: isTirePressureLabel(trimmed) ? TIRE_PRESSURE_NAME : trimmed,
    });
    persistCatalog([...catalog, created]);
    ensureChecklistEnabled();
    if (isTirePressureLabel(created.label)) {
      openTirePressurePopup(created);
    } else {
      onChange([...items, created]);
    }
    setNewLabel("");
  }

  function startEdit(event, item) {
    event.stopPropagation();
    if (isTirePressureLabel(item.label)) {
      setEditingId(item.id);
      openTirePressurePopup(item);
      return;
    }
    setEditingId(item.id);
    setEditingLabel(item.label);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingLabel("");
  }

  function saveEdit(item) {
    const trimmed = editingLabel.trim();
    if (!trimmed) return;
    const duplicate = catalog.some(
      (row) => row.id !== item.id && sameLabel(row.label, trimmed)
    );
    if (duplicate) return;
    const updated = { ...item, label: trimmed };
    const nextCatalog = catalog.map((row) =>
      row.id === item.id ? updated : row
    );
    persistCatalog(nextCatalog);
    onChange(
      items.map((selected) =>
        selected.id === item.id || sameLabel(selected.label, item.label)
          ? { ...selected, label: trimmed }
          : selected
      )
    );
    cancelEdit();
  }

  function removeItem(event, item) {
    event.stopPropagation();
    if (editingId !== item.id) return;
    const nextCatalog = catalog.filter((row) => row.id !== item.id);
    persistCatalog(nextCatalog);
    onChange(
      isTirePressureLabel(item.label)
        ? items.filter((selected) => !isTirePressureLabel(selected.label))
        : items.filter(
            (selected) =>
              selected.id !== item.id && !sameLabel(selected.label, item.label)
          )
    );
    cancelEdit();
    closeTirePressurePopup();
  }

  function saveTirePressure() {
    if (!tirePressureItem || !tirePressureIsValid(tirePressureDraft)) return;
    const machineItem = checklistItem({
      id: selectedTireItem ? selectedTireItem.id : tirePressureItem.id,
      label: formatTirePressureLabel(tirePressureDraft),
      require_photo: Boolean(
        (selectedTireItem && selectedTireItem.require_photo) ||
          tirePressureItem.require_photo
      ),
    });
    onChange([
      ...items.filter((selected) => !isTirePressureLabel(selected.label)),
      machineItem,
    ]);
    closeTirePressurePopup();
  }

  function updateTireDraft(field, value) {
    setTirePressureDraft((current) => ({ ...current, [field]: value }));
  }

  async function saveTemplate() {
    const name = templateName.trim();
    if (!name || !items.length) return;
    const payload = {
      farm: Number(farmId),
      name,
      items,
    };
    const existing = templates.find((row) => sameLabel(row.name, name));
    try {
      if (existing) {
        await axios.patch(
          `${myConfig.API_URL}/checklist-template/${existing.id}/`,
          payload,
          { headers: authHeaders }
        );
      } else {
        await axios.post(
          `${myConfig.API_URL}/checklist-template/`,
          payload,
          { headers: authHeaders }
        );
      }
      await loadTemplates();
      setTemplateMessage(
        t("Checklist salvo. Abra outra máquina e aplique este nome para copiar os itens destacados.")
      );
    } catch (error) {
      setTemplateMessage(t("Não foi possível salvar o checklist."));
    }
  }

  function applyTemplate() {
    const template = templates.find(
      (row) => String(row.id) === String(selectedTemplateId)
    );
    if (!template) return;
    const nextItems = (template.items || []).map((item) => checklistItem(item));
    onChange(nextItems);
    const nextCatalog = [...catalog];
    nextItems.forEach((item) => {
      const label = isTirePressureLabel(item.label)
        ? TIRE_PRESSURE_NAME
        : item.label;
      const exists = nextCatalog.some((row) =>
        isTirePressureLabel(label)
          ? isTirePressureLabel(row.label)
          : sameLabel(row.label, label)
      );
      if (!exists) {
        nextCatalog.push(checklistItem(item, { label }));
      }
    });
    persistCatalog(nextCatalog);
    setTemplateMessage(
      t("Checklist aplicado nesta máquina. Salve a máquina para gravar.")
    );
  }

  return (
    <div className="form-group machine-checklist-editor">
      <div className="machine-checklist-editor__heading">
        <label>
          <input
            type="checkbox"
            checked={Boolean(enabled)}
            onChange={(event) => onEnabledChange(event.target.checked)}
          />{" "}
          {t("Checklist ao assumir a máquina")}
        </label>
        <button
          type="button"
          className="sidebar-menu-info-btn"
          style={{ position: "static", margin: 0 }}
          title={t("Como funciona o checklist ao assumir?")}
          aria-label={t("Como funciona o checklist ao assumir?")}
          onClick={() => setShowHelp(true)}
        >
          <FaInfoCircle />
        </button>
      </div>
      <small className="help-block text-muted">
        {t(
          "Marque a caixinha para ativar. No app, o operador digita o horímetro/hodômetro primeiro; com a leitura, o sistema decide se há item vencido e só então pede o checklist."
        )}
      </small>
      <small className="help-block text-muted">
        {t(
          "Clique no nome para destacar o que vale nesta máquina. Use A cada (h ou km conforme o medidor), Obrigatório e a câmera em cada item. Vazio em A cada = toda vez que assumir."
        )}
      </small>

      <div className="machine-checklist-examples">
        <span className="help-block text-muted" style={{ marginBottom: 6 }}>
          {t("Exemplos — clique para incluir na lista:")}
        </span>
        {DEFAULT_CHECKLIST_EXAMPLES.map((label) => (
          <button
            key={label}
            type="button"
            className="btn btn-xs btn-default"
            style={{ marginRight: 6, marginBottom: 6 }}
            onClick={() => addItem(label)}
          >
            {t(label)}
          </button>
        ))}
      </div>

      {catalog.length ? (
        <ul className="list-unstyled machine-checklist-items">
          {catalog.map((item) => {
            const selected = isSelected(item);
            const editing = editingId === item.id;
            const displayLabel = catalogLabel(item);
            return (
              <li
                key={item.id}
                className={
                  selected
                    ? "machine-checklist-item is-selected"
                    : "machine-checklist-item is-muted"
                }
              >
                {editing && !isTirePressureLabel(item.label) ? (
                  <input
                    type="text"
                    className="form-control machine-checklist-item-input"
                    value={editingLabel}
                    autoFocus
                    onChange={(event) => setEditingLabel(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        saveEdit(item);
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        cancelEdit();
                      }
                    }}
                  />
                ) : (
                  <div className="machine-checklist-item-main">
                    <button
                      type="button"
                      className="machine-checklist-item-toggle"
                      onClick={() => toggleItem(item)}
                    >
                      {displayLabel}
                    </button>
                    {selected && selectedTireItem && isTirePressureLabel(item.label) ? (
                      <small className="machine-checklist-item-detail">
                        {selectedTireItem.label}
                      </small>
                    ) : null}
                  </div>
                )}
                <span className="machine-checklist-item-actions">
                  {selected ? (
                    <label
                      className="machine-checklist-interval"
                      title={t(
                        "A cada: intervalo no horímetro (h) ou hodômetro (km). Vazio = pedir toda vez."
                      )}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <span>{t("A cada")}</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="form-control machine-checklist-interval-input"
                        value={
                          itemInterval(item) == null ? "" : itemInterval(item)
                        }
                        placeholder="—"
                        onChange={(event) => changeInterval(event, item)}
                      />
                      <span>{intervalUnit}</span>
                    </label>
                  ) : null}
                  {selected ? (
                    <button
                      type="button"
                      className={
                        itemIsMandatory(item)
                          ? "machine-checklist-mandatory-btn is-on"
                          : "machine-checklist-mandatory-btn"
                      }
                      title={
                        itemIsMandatory(item)
                          ? t(
                              "Obrigatório: com o item vencido, o app só libera o assume depois da confirmação."
                            )
                          : t(
                              "Opcional: o item pode aparecer no app, mas não bloqueia o assume."
                            )
                      }
                      aria-label={t("Obrigatório")}
                      onClick={(event) => toggleMandatory(event, item)}
                    >
                      {t("Obrigatório")}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={
                      itemRequiresPhoto(item)
                        ? "machine-checklist-photo-btn is-on"
                        : "machine-checklist-photo-btn"
                    }
                    title={
                      itemRequiresPhoto(item)
                        ? t(
                            "Foto ligada: no app o operador precisa fotografar este item."
                          )
                        : t(
                            "Foto desligada: basta confirmar o item, sem foto."
                          )
                    }
                    aria-label={t("Exigir foto")}
                    onClick={(event) => toggleRequirePhoto(event, item)}
                  >
                    <FaCamera />
                  </button>
                  {editing ? (
                    <>
                      {!isTirePressureLabel(item.label) ? (
                        <button
                          type="button"
                          className="btn btn-xs btn-link"
                          onClick={() => saveEdit(item)}
                        >
                          {t("Pronto")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-xs btn-link"
                        onClick={(event) => removeItem(event, item)}
                      >
                        {t("Remover")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-xs btn-link"
                      onClick={(event) => startEdit(event, item)}
                    >
                      {t("Editar")}
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-muted">
          {t("Nenhum nome na lista ainda. Use um exemplo ou crie um item.")}
        </p>
      )}

      <div className="form-inline">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: 360, marginRight: 8 }}
          value={newLabel}
          placeholder={t("Novo item, ex.: Correia do ventilador")}
          onChange={(event) => setNewLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addItem(newLabel);
            }
          }}
        />
        <button
          type="button"
          className="btn btn-default"
          onClick={() => addItem(newLabel)}
        >
          {t("Adicionar item")}
        </button>
      </div>

      <div className="machine-checklist-templates">
        <label>{t("Salvar este checklist")}</label>
        <small className="help-block text-muted">
          {t(
            "Guarde os itens destacados com um nome, por exemplo “Padrão tratores” ou “Colheitadeiras”. Depois, ao editar outra máquina, escolha esse nome para destacar os mesmos itens."
          )}
        </small>
        <div className="form-inline" style={{ marginBottom: 12 }}>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 280, marginRight: 8 }}
            value={templateName}
            placeholder={t("Nome, ex.: Padrão tratores")}
            onChange={(event) => setTemplateName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                saveTemplate();
              }
            }}
          />
          <button
            type="button"
            className="btn btn-default"
            disabled={!templateName.trim() || !items.length}
            onClick={saveTemplate}
          >
            {t("Salvar checklist")}
          </button>
        </div>

        <label>{t("Usar um checklist salvo")}</label>
        <div className="form-inline">
          <select
            className="form-control"
            style={{ maxWidth: 280, marginRight: 8 }}
            value={selectedTemplateId}
            onChange={(event) => setSelectedTemplateId(event.target.value)}
          >
            <option value="">{t("Escolha um checklist salvo")}</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-default"
            disabled={!selectedTemplateId}
            onClick={applyTemplate}
          >
            {t("Aplicar nesta máquina")}
          </button>
        </div>
        {templateMessage ? (
          <small className="help-block">{templateMessage}</small>
        ) : null}
      </div>

      {tirePressureItem ? (
        <div
          className="machine-checklist-modal-backdrop"
          onClick={closeTirePressurePopup}
          role="presentation"
        >
          <div
            className="machine-checklist-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tire-pressure-title"
          >
            <div className="machine-checklist-modal-header">
              <h3 id="tire-pressure-title">{t("Pressão ideal dos pneus")}</h3>
              <button
                type="button"
                className="machine-checklist-modal-close"
                onClick={closeTirePressurePopup}
                aria-label={t("Fechar")}
              >
                ×
              </button>
            </div>
            <div className="machine-checklist-modal-body">
              <p>
                {t(
                  "Não adianta só pedir para conferir a pressão. O operador precisa saber qual é a pressão ideal desta máquina e conferir se ela se mantém."
                )}
              </p>
              <p>
                {t(
                  "Informe a pressão ideal. Se a dianteira for diferente da traseira, preencha as duas. Se o pneu interno for diferente do externo, marque essa opção e preencha cada um."
                )}
              </p>

              <div className="form-group">
                <label>{t("Unidade")}</label>
                <select
                  className="form-control"
                  value={tirePressureDraft.unit}
                  onChange={(event) => updateTireDraft("unit", event.target.value)}
                >
                  <option value="lb">lb</option>
                  <option value="psi">psi</option>
                  <option value="bar">bar</option>
                </select>
              </div>

              <div className="checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={tirePressureDraft.sameAll}
                    onChange={(event) =>
                      updateTireDraft("sameAll", event.target.checked)
                    }
                  />{" "}
                  {t("Mesma pressão em todos os pneus")}
                </label>
              </div>

              {tirePressureDraft.sameAll ? (
                <div className="form-group">
                  <label>{t("Pressão ideal")}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={tirePressureDraft.front}
                    placeholder={t("Ex.: 24")}
                    onChange={(event) => updateTireDraft("front", event.target.value)}
                  />
                </div>
              ) : (
                <>
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={tirePressureDraft.differInnerOuter}
                        onChange={(event) =>
                          updateTireDraft("differInnerOuter", event.target.checked)
                        }
                      />{" "}
                      {t("Interno e externo são diferentes")}
                    </label>
                  </div>
                  {tirePressureDraft.differInnerOuter ? (
                    <div className="machine-checklist-pressure-grid">
                      <div className="form-group">
                        <label>{t("Dianteiro interno")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={tirePressureDraft.frontInner}
                          placeholder={t("Ex.: 24")}
                          onChange={(event) =>
                            updateTireDraft("frontInner", event.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("Dianteiro externo")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={tirePressureDraft.frontOuter}
                          placeholder={t("Ex.: 26")}
                          onChange={(event) =>
                            updateTireDraft("frontOuter", event.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("Traseiro interno")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={tirePressureDraft.rearInner}
                          placeholder={t("Ex.: 28")}
                          onChange={(event) =>
                            updateTireDraft("rearInner", event.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("Traseiro externo")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={tirePressureDraft.rearOuter}
                          placeholder={t("Ex.: 30")}
                          onChange={(event) =>
                            updateTireDraft("rearOuter", event.target.value)
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="machine-checklist-pressure-grid">
                      <div className="form-group">
                        <label>{t("Dianteira")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={tirePressureDraft.front}
                          placeholder={t("Ex.: 24")}
                          onChange={(event) =>
                            updateTireDraft("front", event.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>{t("Traseira")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={tirePressureDraft.rear}
                          placeholder={t("Ex.: 28")}
                          onChange={(event) =>
                            updateTireDraft("rear", event.target.value)
                          }
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="machine-checklist-modal-footer">
              <button
                type="button"
                className="btn btn-default"
                onClick={closeTirePressurePopup}
              >
                {t("Cancelar")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!tirePressureIsValid(tirePressureDraft)}
                onClick={saveTirePressure}
              >
                {t("Usar nesta máquina")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showHelp ? (
        <SidebarToolInfoModal
          title={t("Como funciona o checklist ao assumir?")}
          onClose={() => setShowHelp(false)}
        >
          <p>
            {t(
              "No app, o operador liga a chave, digita o horímetro ou hodômetro e só então o sistema decide se há checklist vencido. A unidade (h ou km) segue o tipo de medidor desta máquina."
            )}
          </p>
          <p>
            <strong>{t("A cada")}</strong>
            {" — "}
            {t(
              "Intervalo no medidor. Ex.: 100 h = o app pede de novo quando a leitura avançar 100 horas desde a última vez que aquele item foi feito. Vazio = pedir toda vez que assumir. Em máquinas com hodômetro, o valor é em km."
            )}
          </p>
          <p>
            <strong>{t("Obrigatório")}</strong>
            {" — "}
            {t(
              "Com o item vencido, o operador precisa confirmar (e fotografar, se a câmera estiver ligada) para liberar o assume. Desligado = o item pode aparecer, mas não trava o trabalho."
            )}
          </p>
          <p>
            <strong>{t("Foto")}</strong>
            {" — "}
            {t(
              "Câmera ligada: o app exige foto daquele item (ex.: borboleta do óleo). Câmera desligada: basta marcar como conferido."
            )}
          </p>
        </SidebarToolInfoModal>
      ) : null}
    </div>
  );
}

export default MachineChecklistEditor;
